import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import AlltagsbegleitungPage from '@/pages/Alltagsbegleitung';
import FensterreinigungPage from '@/pages/Fensterreinigung';
import { 
  Building2, 
  Home as HomeIcon, 
  Trees, 
  Trash2, 
  Mail, 
  Eye, 
  FileText, 
  Sparkles, 
  Phone, 
  MapPin, 
  CheckCircle2, 
  ArrowRight, 
  Menu, 
  X,
  ShieldCheck,
  Clock,
  Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Startseite' },
    { path: '/alltagsbegleitung', label: 'Alltagsbegleitung' },
    { path: '/fensterreinigung', label: 'Fensterreinigung' },
    { path: '/kontakt', label: 'Kontakt' },
    { path: '/impressum', label: 'Impressum' },
    { path: '/datenschutz', label: 'Datenschutz' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="/logo.png"
              alt="BunterKopf Gebäudereinigung Oldenburg Logo"
              className="h-12 md:h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.path 
                    ? 'text-[#0052CC]' 
                    : 'text-slate-600 hover:text-[#0052CC]'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/kontakt"
              className="btn-primary text-sm py-3 px-6"
              data-testid="nav-cta-button"
            >
              Jetzt anfragen
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-600"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={isOpen}
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-slate-100" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 text-sm font-medium ${
                  location.pathname === link.path 
                    ? 'text-[#0052CC]' 
                    : 'text-slate-600'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/kontakt"
              className="btn-primary text-sm py-3 px-6 mt-4 inline-block"
              onClick={() => setIsOpen(false)}
            >
              Jetzt anfragen
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-gradient-to-br from-blue-50 via-white to-blue-50" data-testid="hero-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Left Content */}
        <div className="animate-fade-in-up">
          <span className="inline-block px-4 py-2 bg-[#E6F0FF] text-[#0052CC] text-sm font-medium rounded-full mb-6">
            Lokaler Service in Oldenburg
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Professionelle<br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#0052CC] to-[#0043A8]">
              Reinigungsdienste
            </span><br />
            in Oldenburg
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-lg">
            Ihr zuverlässiger Partner für Büro- und Unterhaltsreinigung, Treppenhausreinigung 
            und mehr. Für kleine und mittelständische Unternehmen, Kanzleien und Hausverwaltungen.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/kontakt" className="btn-primary text-center" data-testid="hero-cta-primary">
              Kostenlose Anfrage stellen
            </Link>
            <a href="#leistungen" className="btn-secondary text-center" data-testid="hero-cta-secondary">
              Unsere Leistungen
            </a>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap gap-6 mt-10">
            <div className="flex items-center gap-2 text-slate-600">
              <ShieldCheck className="w-5 h-5 text-[#00C2B8]" />
              <span className="text-sm font-medium">Zuverlässig</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Clock className="w-5 h-5 text-[#00C2B8]" />
              <span className="text-sm font-medium">Pünktlich</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Users className="w-5 h-5 text-[#00C2B8]" />
              <span className="text-sm font-medium">Persönlich</span>
            </div>
          </div>
        </div>

        {/* Right Image */}
        <div className="relative animate-fade-in-up animate-delay-200">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1588084580191-db8604397b3d?crop=entropy&cs=srgb&auto=format&fit=crop&q=70&w=800"
              alt="Professionelle Büroreinigung in Oldenburg durch BunterKopf Gebäudereinigung"
              className="w-full h-[400px] lg:h-[500px] object-cover"
              width="800"
              height="500"
              fetchpriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
          </div>
          
          {/* Floating Card */}
          <div className="glass-card absolute -bottom-6 -left-6 md:left-auto md:-right-6 shadow-xl" data-testid="satisfaction-badge">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#00C2B8] rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Zufriedenheit</p>
                <p className="text-sm text-slate-500">garantiert</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Building2,
      title: 'Büro- & Unterhaltsreinigung',
      description: 'Regelmäßige professionelle Reinigung für Ihr Büro. Wir sorgen für eine saubere und hygienische Arbeitsumgebung.',
      image: 'https://images.unsplash.com/photo-1596022326813-27f929ca80ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      featured: true
    },
    {
      icon: HomeIcon,
      title: 'Treppenhausreinigung',
      description: 'Saubere Treppenhäuser und Eingangsbereiche für einen gepflegten ersten Eindruck.',
      image: 'https://images.unsplash.com/photo-1572598142963-5050ea0d4a5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      featured: false
    },
    {
      icon: Trees,
      title: 'Laubentfernung',
      description: 'Saisonale Laubentfernung für gepflegte Außenbereiche und sichere Wege.',
      image: 'https://images.unsplash.com/photo-1601058660898-82c168404c88?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      featured: false
    },
    {
      icon: Trash2,
      title: 'Mülltonnendienst',
      description: 'Zuverlässige Bereitstellung und Rückholung Ihrer Mülltonnen.',
      image: null,
      featured: false
    }
  ];

  return (
    <section id="leistungen" className="py-20 md:py-32 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#E6F0FF] text-[#0052CC] text-sm font-medium rounded-full mb-4">
            Unsere Leistungen
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Umfassende Reinigungsdienste
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Von der täglichen Büroreinigung bis zur saisonalen Laubentfernung – wir bieten alles aus einer Hand.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
          {/* Featured Service */}
          <div className="lg:col-span-8 service-card group" data-testid="service-buero">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
              <div className="flex flex-col justify-center">
                <div className="w-14 h-14 bg-[#E6F0FF] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0052CC] transition-colors duration-300">
                  <Building2 className="w-7 h-7 text-[#0052CC] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Büro- & Unterhaltsreinigung
                </h3>
                <p className="text-slate-600 mb-4">
                  Regelmäßige professionelle Reinigung für Ihr Büro. Wir sorgen für eine saubere und hygienische Arbeitsumgebung.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-[#00C2B8]" />
                    <span className="text-sm">Tägliche oder wöchentliche Reinigung</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-[#00C2B8]" />
                    <span className="text-sm">Sanitäranlagen & Küchen</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-[#00C2B8]" />
                    <span className="text-sm">Bodenreinigung & Staubwischen</span>
                  </li>
                </ul>
              </div>
              <div className="relative rounded-xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1596022326813-27f929ca80ab?crop=entropy&cs=srgb&auto=format&fit=crop&q=70&w=600"
                  alt="Büroreinigung in Oldenburg"
                  className="w-full h-full min-h-[250px] object-cover group-hover:scale-105 transition-transform duration-500"
                  width="600"
                  height="400"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>

          {/* Treppenhausreinigung */}
          <div className="lg:col-span-4 service-card group" data-testid="service-treppenhaus">
            <div className="w-12 h-12 bg-[#E6F0FF] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0052CC] transition-colors duration-300">
              <HomeIcon className="w-6 h-6 text-[#0052CC] group-hover:text-white transition-colors duration-300" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Treppenhausreinigung
            </h3>
            <p className="text-slate-600 text-sm mb-5">
              Saubere Treppenhäuser und Eingangsbereiche für einen gepflegten ersten Eindruck.
            </p>
            <div className="rounded-xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1580911498851-4999ad5327b6?crop=entropy&cs=srgb&auto=format&fit=crop&q=70&w=600"
                alt="Sauberes, gepflegtes Treppenhaus in Oldenburg"
                className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500"
                width="600"
                height="300"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          {/* Laubentfernung */}
          <div className="lg:col-span-4 service-card group" data-testid="service-laub">
            <div className="w-12 h-12 bg-[#E6F0FF] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0052CC] transition-colors duration-300">
              <Trees className="w-6 h-6 text-[#0052CC] group-hover:text-white transition-colors duration-300" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Laubentfernung
            </h3>
            <p className="text-slate-600 text-sm">
              Saisonale Laubentfernung für gepflegte Außenbereiche und sichere Wege.
            </p>
          </div>

          {/* Mülltonnendienst */}
          <div className="lg:col-span-4 service-card group" data-testid="service-muell">
            <div className="w-12 h-12 bg-[#E6F0FF] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0052CC] transition-colors duration-300">
              <Trash2 className="w-6 h-6 text-[#0052CC] group-hover:text-white transition-colors duration-300" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Mülltonnendienst
            </h3>
            <p className="text-slate-600 text-sm">
              Zuverlässige Bereitstellung und Rückholung Ihrer Mülltonnen.
            </p>
          </div>

          {/* Fensterreinigung */}
          <div className="lg:col-span-4 service-card group flex flex-col" data-testid="service-fensterreinigung">
            <div className="w-12 h-12 bg-[#E6F0FF] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0052CC] transition-colors duration-300">
              <Sparkles className="w-6 h-6 text-[#0052CC] group-hover:text-white transition-colors duration-300" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Fensterreinigung
            </h3>
            <p className="text-slate-600 text-sm mb-5">
              Streifenfrei sauber – Glas innen &amp; außen inkl. Rahmen. Ab 10 € pro Fenster.
            </p>
            <Link
              to="/fensterreinigung#konfigurator"
              className="mt-auto inline-flex items-center gap-2 bg-[#0052CC] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#0041A3] transition-colors duration-300 self-start"
              data-testid="service-fensterreinigung-configure"
            >
              Jetzt konfigurieren <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* CTA Card */}
          <div className="lg:col-span-12 bg-[#0052CC] p-8 md:p-10 rounded-2xl text-white flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="services-cta-card">
            <div>
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Individuelles Angebot
              </h3>
              <p className="text-blue-100 text-sm">
                Wir erstellen Ihnen ein maßgeschneidertes Angebot – kostenlos und unverbindlich.
              </p>
            </div>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 bg-white text-[#0052CC] px-6 py-3 rounded-full font-medium hover:bg-blue-50 transition-colors duration-300 whitespace-nowrap self-start md:self-auto"
            >
              Jetzt anfragen
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      icon: Mail,
      title: 'Anfrage',
      description: 'Senden Sie uns Ihre Anfrage über das Kontaktformular.'
    },
    {
      icon: Eye,
      title: 'Besichtigung',
      description: 'Wir besichtigen Ihr Objekt und besprechen Ihre Wünsche.'
    },
    {
      icon: FileText,
      title: 'Angebot',
      description: 'Sie erhalten ein individuelles und transparentes Angebot.'
    },
    {
      icon: Sparkles,
      title: 'Service',
      description: 'Wir starten mit der professionellen Reinigung.'
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-slate-50" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#E6F0FF] text-[#0052CC] text-sm font-medium rounded-full mb-4">
            So funktioniert's
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            In 4 Schritten zu Ihrer Reinigung
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Einfach, transparent und unkompliziert – so arbeiten wir mit Ihnen zusammen.
          </p>
        </div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connector Line (Desktop) */}
          <div className="process-line hidden md:block"></div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative text-center" data-testid={`process-step-${index + 1}`}>
                {/* Step Number & Icon */}
                <div className="relative inline-block mb-6">
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto border border-slate-100">
                    <step.icon className="w-7 h-7 text-[#0052CC]" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-7 h-7 bg-[#0052CC] text-white text-sm font-bold rounded-full flex items-center justify-center">
                    {index + 1}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Link to="/kontakt" className="btn-primary inline-flex items-center gap-2" data-testid="process-cta">
            Jetzt Anfrage starten
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Target Audience Section
const AudienceSection = () => {
  const audiences = [
    {
      title: 'Büros & Unternehmen',
      description: 'Kleine und mittelständische Betriebe',
      icon: Building2
    },
    {
      title: 'Kanzleien & Praxen',
      description: 'Anwaltskanzleien, Arztpraxen und mehr',
      icon: FileText
    },
    {
      title: 'Wohnungseigentümer',
      description: 'Private Mehrfamilienhäuser',
      icon: HomeIcon
    },
    {
      title: 'Hausverwaltungen',
      description: 'Professionelle Objektbetreuung',
      icon: Users
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-white" data-testid="audience-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div>
            <span className="inline-block px-4 py-2 bg-[#E6F0FF] text-[#0052CC] text-sm font-medium rounded-full mb-4">
              Für wen wir da sind
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ihr Partner für professionelle Gebäudereinigung
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Wir unterstützen lokale Unternehmen und Eigentümer in Oldenburg mit zuverlässigen 
              und hochwertigen Reinigungsdienstleistungen.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {audiences.map((audience, index) => (
                <div key={index} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="w-10 h-10 bg-[#E6F0FF] rounded-lg flex items-center justify-center flex-shrink-0">
                    <audience.icon className="w-5 h-5 text-[#0052CC]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{audience.title}</h3>
                    <p className="text-sm text-slate-500">{audience.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1572598142963-5050ea0d4a5c?crop=entropy&cs=srgb&auto=format&fit=crop&q=70&w=800"
                alt="Gepflegtes Gebäude in Oldenburg"
                className="w-full h-[400px] object-cover"
                width="800"
                height="400"
                loading="lazy"
                decoding="async"
              />
            </div>
            {/* Stats Card */}
            <div className="glass-card absolute -bottom-6 -left-6 shadow-xl" data-testid="local-badge">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0052CC] rounded-full flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">100% Lokal</p>
                  <p className="text-sm text-slate-500">in Oldenburg</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Reviews Section – Google-Bewertungen via Elfsight Widget
const ReviewsSection = () => {
  useEffect(() => {
    const scriptId = 'elfsight-platform-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://elfsightcdn.com/platform.js';
      script.async = true;
      document.body.appendChild(script);
    } else if (window.eapps && typeof window.eapps.initWidgetsFromBuffer === 'function') {
      window.eapps.initWidgetsFromBuffer();
    }
  }, []);

  return (
    <section className="py-20 md:py-32 bg-slate-50" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-[#E6F0FF] text-[#0052CC] text-sm font-medium rounded-full mb-4">
            Kundenstimmen
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Was unsere Kunden sagen
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Echte Google-Bewertungen unserer Kundinnen und Kunden – aktualisieren sich automatisch.
          </p>
        </div>

        <div
          className="elfsight-app-7a1a6f77-c33c-4097-8988-44fee46f2e4d"
          data-elfsight-app-lazy
          data-testid="elfsight-reviews-widget"
        ></div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section className="py-20 md:py-32 bg-[#0052CC]" data-testid="cta-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Bereit für eine saubere Lösung?
      </h2>
      <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
        Kontaktieren Sie uns noch heute für ein kostenloses und unverbindliches Angebot. 
        Wir freuen uns auf Ihre Anfrage!
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link
          to="/kontakt"
          className="inline-flex items-center justify-center gap-2 bg-white text-[#0052CC] px-8 py-4 rounded-full font-medium hover:bg-blue-50 transition-colors duration-300 shadow-lg"
          data-testid="cta-primary-button"
        >
          Kostenlos anfragen
          <ArrowRight className="w-5 h-5" />
        </Link>
        <a
          href="tel:044193925989"
          className="inline-flex items-center justify-center gap-2 bg-transparent text-white border-2 border-white/30 px-8 py-4 rounded-full font-medium hover:bg-white/10 transition-colors duration-300"
          data-testid="cta-phone-button"
        >
          <Phone className="w-5 h-5" />
          0441 93925989
        </a>
      </div>
    </div>
  </section>
);

// Contact Form Component
const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="input-field"
            placeholder="Ihr Name"
            data-testid="contact-name-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">E-Mail *</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="input-field"
            placeholder="ihre@email.de"
            data-testid="contact-email-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Telefon</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="input-field"
            placeholder="0441 123456"
            data-testid="contact-phone-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Unternehmen</label>
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="input-field"
            placeholder="Ihr Unternehmen"
            data-testid="contact-company-input"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Gewünschte Leistung</label>
        <select
          name="service"
          value={formData.service}
          onChange={handleChange}
          className="select-field"
          data-testid="contact-service-select"
        >
          <option value="">Bitte wählen...</option>
          <option value="buero">Büro- & Unterhaltsreinigung</option>
          <option value="treppenhaus">Treppenhausreinigung</option>
          <option value="laub">Laubentfernung</option>
          <option value="muell">Mülltonnendienst</option>
          <option value="sonstiges">Sonstiges</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Ihre Nachricht *</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          className="textarea-field"
          placeholder="Beschreiben Sie Ihr Anliegen..."
          data-testid="contact-message-textarea"
        ></textarea>
      </div>

      {submitStatus === 'success' && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700" data-testid="contact-success-message">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            <span>Vielen Dank! Ihre Anfrage wurde erfolgreich gesendet. Wir melden uns zeitnah bei Ihnen.</span>
          </div>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700" data-testid="contact-error-message">
          Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns telefonisch.
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="contact-submit-button"
      >
        {isSubmitting ? 'Wird gesendet...' : 'Anfrage absenden'}
        {!isSubmitting && <ArrowRight className="w-5 h-5" />}
      </button>
    </form>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-slate-900 text-white py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        {/* Logo & Info */}
        <div className="md:col-span-2">
          <div className="inline-block bg-white rounded-xl p-3 mb-4">
            <img
              src="/logo.png"
              alt="BunterKopf Gebäudereinigung Logo"
              className="h-12 w-auto"
            />
          </div>
          <p className="text-slate-300 mb-6 max-w-md">
            Ihr zuverlässiger Partner für professionelle Gebäudereinigung in Oldenburg. 
            Qualität und Kundenzufriedenheit stehen bei uns an erster Stelle.
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-3 text-slate-400">
              <Mail className="w-5 h-5 text-[#0052CC]" />
              <a href="mailto:service@bunterkopf-reinigung.de" className="hover:text-white transition-colors">
                service@bunterkopf-reinigung.de
              </a>
            </div>
            <div className="flex items-center gap-3 text-slate-400">
              <Phone className="w-5 h-5 text-[#0052CC]" />
              <a href="tel:044193925989" className="hover:text-white transition-colors">
                0441 93925989
              </a>
            </div>
            <div className="flex items-start gap-3 text-slate-400">
              <MapPin className="w-5 h-5 text-[#0052CC] mt-0.5" />
              <div>
                <span className="block">Oldenburg (Hauptstandort)</span>
                <span className="block">Aurich</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h3 className="font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Navigation</h3>
          <ul className="space-y-2">
            <li><Link to="/" className="text-slate-400 hover:text-white transition-colors">Startseite</Link></li>
            <li><a href="/#leistungen" className="text-slate-400 hover:text-white transition-colors">Leistungen</a></li>
            <li><Link to="/kontakt" className="text-slate-400 hover:text-white transition-colors">Kontakt</Link></li>
          </ul>
        </div>

        {/* Legal */}
        <div>
          <h3 className="font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Rechtliches</h3>
          <ul className="space-y-2">
            <li><Link to="/impressum" className="text-slate-400 hover:text-white transition-colors">Impressum</Link></li>
            <li><Link to="/datenschutz" className="text-slate-400 hover:text-white transition-colors">Datenschutz</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-400 text-sm">
        © {new Date().getFullYear()} BunterKopf Gebäudereinigung. Alle Rechte vorbehalten.
      </div>
    </div>
  </footer>
);

// Page Components
const HomePage = () => (
  <>
    <HeroSection />
    <ServicesSection />
    <ProcessSection />
    <AudienceSection />
    <ReviewsSection />
    <CTASection />
  </>
);

const KontaktPage = () => (
  <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen" data-testid="kontakt-page">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        {/* Left - Contact Info */}
        <div>
          <span className="inline-block px-4 py-2 bg-[#E6F0FF] text-[#0052CC] text-sm font-medium rounded-full mb-6">
            Kontakt
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Sprechen Sie<br />mit uns
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed mb-8">
            Haben Sie Fragen oder möchten Sie ein unverbindliches Angebot? 
            Wir freuen uns auf Ihre Nachricht!
          </p>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#E6F0FF] rounded-xl flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-[#0052CC]" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">E-Mail</h3>
                <a href="mailto:service@bunterkopf-reinigung.de" className="text-slate-600 hover:text-[#0052CC] transition-colors">
                  service@bunterkopf-reinigung.de
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#E6F0FF] rounded-xl flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-[#0052CC]" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Telefon</h3>
                <a href="tel:044193925989" className="text-slate-600 hover:text-[#0052CC] transition-colors">
                  0441 93925989
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#E6F0FF] rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-[#0052CC]" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Standorte</h3>
                <div className="space-y-2 mt-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-block px-2 py-0.5 bg-[#0052CC] text-white text-xs font-medium rounded">Hauptstandort</span>
                    <span className="text-slate-600">Oldenburg</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block px-2 py-0.5 bg-slate-200 text-slate-700 text-xs font-medium rounded">Standort</span>
                    <span className="text-slate-600">Aurich</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right - Contact Form */}
        <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-slate-100">
          <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Anfrage senden
          </h2>
          <p className="text-slate-600 mb-8">Alle mit * markierten Felder sind Pflichtfelder.</p>
          <ContactForm />
        </div>
      </div>
    </div>
  </section>
);

const ImpressumPage = () => (
  <section className="pt-32 pb-20 md:pt-40 md:pb-32 min-h-screen" data-testid="impressum-page">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Impressum
      </h1>
      
      <div className="prose prose-slate max-w-none">
        <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Angaben gemäß § 5 TMG</h2>
        <p className="text-slate-600 mb-4">
          BunterKopf UG (haftungsbeschränkt)<br />
          Feldhuser Weg 2<br />
          26629 Großefehn
        </p>

        <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Kontakt</h2>
        <p className="text-slate-600 mb-4">
          Telefon: 0441 93925989<br />
          E-Mail: service@bunterkopf-reinigung.de
        </p>

        <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Haftung für Inhalte</h2>
        <p className="text-slate-600 mb-4">
          Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den 
          allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht 
          verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen 
          zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
        </p>

        <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Haftung für Links</h2>
        <p className="text-slate-600 mb-4">
          Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. 
          Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der 
          verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
        </p>

        <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Urheberrecht</h2>
        <p className="text-slate-600 mb-4">
          Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem 
          deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung 
          außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen 
          Autors bzw. Erstellers.
        </p>
      </div>
    </div>
  </section>
);

const DatenschutzPage = () => (
  <section className="pt-32 pb-20 md:pt-40 md:pb-32 min-h-screen" data-testid="datenschutz-page">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Datenschutzerklärung
      </h1>
      
      <div className="prose prose-slate max-w-none">
        <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">1. Datenschutz auf einen Blick</h2>
        <h3 className="text-lg font-medium text-slate-900 mt-6 mb-3">Allgemeine Hinweise</h3>
        <p className="text-slate-600 mb-4">
          Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen 
          Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen 
          Sie persönlich identifiziert werden können.
        </p>

        <h3 className="text-lg font-medium text-slate-900 mt-6 mb-3">Datenerfassung auf dieser Website</h3>
        <p className="text-slate-600 mb-4">
          <strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong><br />
          Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
          können Sie dem Impressum dieser Website entnehmen.
        </p>

        <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">2. Hosting</h2>
        <p className="text-slate-600 mb-4">
          Wir hosten die Inhalte unserer Website bei folgendem Anbieter. Die erhobenen Daten werden auf den 
          Servern des Anbieters verarbeitet.
        </p>

        <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">3. Allgemeine Hinweise und Pflichtinformationen</h2>
        <h3 className="text-lg font-medium text-slate-900 mt-6 mb-3">Datenschutz</h3>
        <p className="text-slate-600 mb-4">
          Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln 
          Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften 
          sowie dieser Datenschutzerklärung.
        </p>

        <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">4. Datenerfassung auf dieser Website</h2>
        <h3 className="text-lg font-medium text-slate-900 mt-6 mb-3">Kontaktformular</h3>
        <p className="text-slate-600 mb-4">
          Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem 
          Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der 
          Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht 
          ohne Ihre Einwilligung weiter.
        </p>

        <h3 className="text-lg font-medium text-slate-900 mt-6 mb-3">Ihre Rechte</h3>
        <p className="text-slate-600 mb-4">
          Sie haben jederzeit das Recht auf unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen 
          Daten, deren Herkunft und Empfänger und den Zweck der Datenverarbeitung sowie ein Recht auf 
          Berichtigung oder Löschung dieser Daten.
        </p>
      </div>
    </div>
  </section>
);

function App() {
  return (
    <div className="App" data-testid="app-container">
      <BrowserRouter>
        <Navigation />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/alltagsbegleitung" element={<AlltagsbegleitungPage />} />
            <Route path="/fensterreinigung" element={<FensterreinigungPage />} />
            <Route path="/kontakt" element={<KontaktPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
